import { motion } from "framer-motion";
import { Check, Info } from "lucide-react";
import { Reveal, SectionLabel, EASE } from "./Reveal";

const FUNCTIONS = [
  ["Leadership", 86],
  ["Product", 82],
  ["Support", 79],
  ["Sales", 74],
  ["Operations", 68],
  ["Finance", 61],
  ["People", 57],
  ["Legal", 43],
];

const LEVELS = [
  ["1", "Intern", "Knows isolated facts, retrieves basic answers"],
  ["2", "Junior", "Answers within individual sources and functions"],
  ["3", "Senior", "Synthesizes multiple sources into grounded recommendations"],
  ["4", "Director", "Reasons across functions and dependencies"],
  ["5", "CXO", "Supports strategic, cross-functional decisions and tradeoffs"],
];

const PATH = [
  ["Connect board decks & strategy documents", "+7%"],
  ["Connect Salesforce — CRM & customer health", "+6%"],
  ["Add leadership decision history", "+5%"],
  ["Connect finance models, budgets & actuals", "+4%"],
  ["Map owners across every function", "+4%"],
];

const COUNTED = [
  ["Policy library", "+9%"],
  ["Product documentation", "+8%"],
  ["Support knowledge base", "+7%"],
  ["Org chart", "+5%"],
  ["SOPs & playbooks", "+4%"],
  ["Shared drive indexing", "+3%"],
];

const Ring = () => {
  const C = 2 * Math.PI * 80;
  return (
    <div className="relative shrink-0" data-testid="org-iq-ring">
      <svg width="190" height="190" viewBox="0 0 200 200" className="-rotate-90">
        <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="17" />
        <motion.circle
          cx="100" cy="100" r="80" fill="none" stroke="#000" strokeWidth="17"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          whileInView={{ strokeDashoffset: C * (1 - 0.72) }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: EASE }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[44px] font-extrabold leading-none tracking-tighter" data-testid="org-iq-score">72%</span>
        <span className="mt-2 text-[9px] font-bold uppercase tracking-[0.2em] text-black/50">Organization IQ</span>
      </div>
    </div>
  );
};

export const OrganizationIQ = () => (
  <section id="org-iq" data-testid="org-iq-section" className="bg-white">
    <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <SectionLabel index="04" title="Organization IQ" />

      <Reveal className="mt-10">
        <h2 className="max-w-4xl text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">
          Not a vanity score. A measure of how ready your company is to decide.{" "}
          <span className="text-black/40">We measure decision readiness — not document completeness.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-6 max-w-2xl">
        <p className="text-base leading-relaxed text-black/70 md:text-lg">
          Organization IQ answers three questions instantly: how intelligent is Qwiri about your
          organization today, where are the gaps, and what should you connect next to make it smarter.
          A source earns weight by importance, freshness and the decision context it unlocks — another
          random PDF moves the needle far less than your CRM or your executive decision history.
        </p>
      </Reveal>

      <Reveal delay={0.12} className="mt-12">
        <p className="max-w-3xl text-xl font-bold leading-snug tracking-tight md:text-2xl" data-testid="org-iq-punchline">
          72% · Senior — Qwiri can confidently support operational decisions across 6 of 8 functions.{" "}
          <span className="text-black/40">+8% to Director.</span>
        </p>
      </Reveal>

      <Reveal delay={0.15} className="mt-10">
        <div className="hard-shadow border-2 border-black bg-white" data-testid="org-iq-dashboard">
          <div className="flex items-center justify-between border-b-2 border-black px-6 py-4 md:px-8">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Organization IQ — Acme Corp</span>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40">Decision readiness</span>
          </div>

          <div className="grid lg:grid-cols-2">
            <div className="flex flex-col gap-8 border-b-2 border-black p-6 sm:flex-row sm:items-center md:p-10 lg:border-b-0 lg:border-r-2">
              <Ring />
              <div>
                <h3 className="text-2xl font-extrabold tracking-tight md:text-3xl">Readiness level: Senior</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-black/60 md:text-base">
                  Based on source coverage, evidence quality, and cross-functional connectivity.
                </p>
                <div className="mt-6 border-t border-black/15 pt-5">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50">Next milestone</div>
                  <div className="mt-1 text-3xl font-extrabold tracking-tighter">
                    8% <span className="text-base font-bold tracking-normal text-black/50">to Director</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-10">
              <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-black/50">
                Decision intelligence level
              </div>
              <div className="divide-y divide-black/10 border border-black/20">
                {LEVELS.map(([n, name, desc]) => (
                  <div
                    key={n}
                    data-testid={`org-iq-level-${name.toLowerCase()}`}
                    className={`grid grid-cols-[36px_92px_1fr] items-center gap-3 px-4 py-3.5 ${
                      name === "Senior" ? "bg-black text-white" : ""
                    }`}
                  >
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                      name === "Senior" ? "bg-white text-black" : "bg-black/10"
                    }`}>
                      {n}
                    </span>
                    <span className="text-sm font-bold">{name}</span>
                    <span className={`text-xs md:text-sm ${name === "Senior" ? "text-white/70" : "text-black/50"}`}>
                      {desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid border-t-2 border-black md:grid-cols-3">
            <div className="border-b-2 border-black p-6 md:border-b-0 md:border-r-2 md:p-8" data-testid="org-iq-functions">
              <div className="mb-5 text-[11px] font-bold uppercase tracking-[0.2em] text-black/50">
                Function readiness
              </div>
              <div className="space-y-3.5">
                {FUNCTIONS.map(([name, v], i) => (
                  <div key={name} className="flex items-center gap-3">
                    <span className="w-24 shrink-0 text-sm font-semibold">{name}</span>
                    <div className="h-2.5 flex-1 bg-black/10">
                      <motion.div
                        className="h-full bg-black"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${v}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: i * 0.05, ease: EASE }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right text-sm font-bold">{v}%</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-black/50">
                Leadership runs at 86% because strategy docs, board decks and decisions are connected.
                Legal sits at 43% — contracts, policies and legal decision history are missing.
              </p>
            </div>

            <div className="border-b-2 border-black p-6 md:border-b-0 md:border-r-2 md:p-8" data-testid="org-iq-path">
              <div className="mb-5 flex items-baseline justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/50">Fastest path to Director</span>
              </div>
              <div className="divide-y divide-black/10">
                {PATH.map(([label, delta], i) => (
                  <div key={label} className="flex items-center gap-3 py-3.5">
                    <span className="h-4 w-4 shrink-0 rounded-full border-2 border-black" />
                    <span className="flex-1 text-sm font-semibold leading-snug">{label}</span>
                    <span className="shrink-0 text-sm font-extrabold">{delta}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-black/50">
                <Info size={14} className="mt-0.5 shrink-0" />
                Never just a number — the score always shows what is missing and what fixing it is worth.
              </p>
            </div>

            <div className="p-6 md:p-8" data-testid="org-iq-counted">
              <div className="mb-5 text-[11px] font-bold uppercase tracking-[0.2em] text-black/50">
                Already counted
              </div>
              <div className="divide-y divide-black/10">
                {COUNTED.map(([label, delta]) => (
                  <div key={label} className="flex items-center gap-3 py-3.5">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-black">
                      <Check size={10} strokeWidth={4} className="text-white" />
                    </span>
                    <span className="flex-1 text-sm font-semibold">{label}</span>
                    <span className="shrink-0 text-sm font-extrabold text-black/60">{delta}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-black/50">
                <Info size={14} className="mt-0.5 shrink-0" />
                These sources are already contributing — progress you can explain, not a black box.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2} className="mt-10">
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-black/50 md:text-[13px]" data-testid="org-iq-note">
          Percentages shown are illustrative — the scoring framework ships with the product
        </p>
      </Reveal>
    </div>
  </section>
);
